<template>
  <div class="flex flex-col items-center justify-center min-h-[60vh] w-full">
    <div v-if="isTokenLoading" class="flex items-center justify-center min-h-[40vh]">
      <span class="text-gray-500 text-lg">{{ t('resetPassword.loading') }}</span>
    </div>
    <InvalidToken v-else-if="isTokenInValid" />
    <PasswordChanged v-else-if="passwordChanged" />
    <div v-else class="flex flex-col items-center w-full max-w-md p-8">
        <h2 class="text-2xl font-semibold text-gray-900 w-full text-left mb-2">
            {{ t("resetPassword.title") }}
        </h2>
        <div class="flex flex-col items-center gap-6 w-full max-w-md bg-white shadow-md p-8">
            <div class="w-full text-left mb-4">
                <h2 class="text-lg font-semibold text-gray-800 mb-1">
                    {{ t("resetPassword.subtitle") }}
                </h2>
                <p class="text-gray-700 text-sm">{{ t("resetPassword.hint") }}</p>
            </div>
            <ResetPasswordForm :passwordRules="passwordRules"
                               @submit="handleFormSubmit" />
        </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";
import authenticationService from "@/services/authenticationService";
import PasswordChanged from "@/components/PasswordChanged.vue";
import InvalidToken from "@/components/InvalidToken.vue";
import ResetPasswordForm from "@/components/PasswordForm.vue";

const { t } = useI18n();

const passwordChanged = ref(false);

const passwordRules = [
  {
    key: "password.length",
    regex: /.{8,}/,
    checklist: () => t("resetPassword.checklist.length"),
  },
  {
    key: "password.uppercase",
    regex: /[A-Z]/,
    checklist: () => t("resetPassword.checklist.uppercase"),
  },
  {
    key: "password.lowercase",
    regex: /[a-z]/,
    checklist: () => t("resetPassword.checklist.lowercase"),
  },
  {
    key: "password.number",
    regex: /[0-9]/,
    checklist: () => t("resetPassword.checklist.number"),
  },
  // Add more rules here as needed
];

function handleFormSubmit({ newPassword: np, confirmPassword: cp, allValid }) {
  if (!np || !cp) {
    alert(t("resetPassword.errors.fillFields"));
    return;
  }
  if (np !== cp) {
    alert(t("resetPassword.errors.mismatch"));
    return;
  }
  if (!allValid) {
    alert(t("resetPassword.errors.requirements"));
    return;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const userId = urlParams.get("userid");
  const code = urlParams.get("code");

  var data = {
    userId,
    code,
    password: np,
    confirmPassword: cp,
  };
  authenticationService
    .resetPassword(data)
    .then((response) => {
      if (response.success) {
        passwordChanged.value = true;
      }else{
        isTokenInValid.value = response.isInvalidCode;
      }
    })
    .catch((error) => {
      console.error(error);
    });
}


const isTokenInValid = ref(false); // true = invalid, false = valid
const isTokenLoading = ref(true);

async function validateTokenBeforeMount() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const model = {
      userId: urlParams.get("userid"),
      code: urlParams.get("code"),
    };
    const result = await authenticationService.resetPasswordCodeValidation(model);
    isTokenInValid.value = !result;
  } catch (e) {
    console.error(e);
    isTokenInValid.value = true;
  } finally {
    isTokenLoading.value = false;
  }
}

onBeforeMount(validateTokenBeforeMount);


</script>

<style scoped>
.text-white {
  color: #fff;
}

.text-green-600 {
  color: #16a34a !important;
}
</style>
